import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { isArray } from 'lodash-es';
import {
  InsuranceQuoteService as _iq,
  IQErrorReason,
  SubmitQuoteError,
} from '@/services/store/insurance-quote.service';
import {
  InsuranceQuotePhase,
  InsuranceQuoteType,
  IPolicyHolderDetails,
  IQuote,
  ISubmitQuote,
  QuoteTypeCodes,
} from '@/interfaces';
import { useIQOriginalQuoteStore, useAnalyticsStore } from '@/store';
import { FormEvents } from '@/enums/form-events.enum';
import { JP_GENERIC_CODE, JP_POLICY_PAID_LATER } from '@/constants/jeopardy-codes.const';
import { PaymentTypes } from '@/enums/payment-types.enum';
import { getQuoteHasBeenSubmitted, setQuoteSubmitted } from '@/utils/local-storage';
import { quoteComplete } from '@/utils/insurance/policy-helper';
import { mapPrimaryPolicyholderAddress } from '@/mappers/policyholder.mapper';

export const useCarPurchaseStore = defineStore('CarPurchase', () => {
  const analyticsStore = useAnalyticsStore();

  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const quote = ref<Partial<IQuote>>({});
  const sessionId = ref<string>('');
  const quoteId = ref<string>('');
  const quoteType = computed<string>(() => (quote.value.quoteType && QuoteTypeCodes[quote.value.quoteType]) || '');
  const updatePostalAddressRequired = ref(false);
  const originalQuoteStore = useIQOriginalQuoteStore();
  const jeopardies = ref<IQErrorReason[]>([]);
  const submitQuoteErrors = ref<SubmitQuoteError[]>([]);
  const premiumHasChanged = ref(false);

  const originalQuote = computed(() => {
    return originalQuoteStore.quote;
  });

  const isMC = computed(() => {
    return quote.value.quoteType == InsuranceQuoteType.MotorComprehensive;
  });

  const isMT = computed(() => {
    return quote.value.quoteType == InsuranceQuoteType.MotorThirdPartyLiability;
  });

  const isMF = computed(() => {
    return quote.value.quoteType == InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability;
  });

  const refreshOriginalQuote = () => {
    originalQuoteStore.setQuote(quote.value, true);
  };

  const fetchQuote = async () => {
    // get quote id
    const params = new URLSearchParams(window.location.search);
    sessionId.value = params.get('ref') ?? '';
    if (!sessionId.value) throw 400;
    const res = await _iq.fetchQuote(sessionId.value);
    quote.value = res?.quote;
    quoteId.value = res?.quote.id;

    originalQuoteStore.setQuote(quote.value);
    if (quote.value.paymentDueDate || quote.value.paymentType == PaymentTypes.PayLater) throw JP_POLICY_PAID_LATER;
    else if (quote.value.policyReference) throw JP_GENERIC_CODE; // throw error if quote already bound
  };

  /**
   * Create new quote
   *
   * @param quote
   */
  const createQuote = async () => {
    try {
      const response = await _iq.createQuote(quote.value);
      if (response.quote) {
        quote.value = response.quote;
        quoteId.value = response.quote.id;
      }
    } catch (error) {
      await handleError(error);
    }
  };

  /**
   * Apply Business Rules
   *
   * @param formData
   */
  const applyBusinessRules = async () => {
    try {
      if (quote?.value?.id && getQuoteHasBeenSubmitted(quote.value.id)) {
        throw JP_GENERIC_CODE;
      }

      const response = await _iq.applyBusinessRules(quoteId.value, quote.value);

      jeopardies.value = response?.reasonCodes || [];
      if (jeopardies.value.length > 0 && jeopardies.value.some((j) => j.typeOfReason === 0)) {
        throw jeopardies.value.find((j) => j.typeOfReason === 0)?.code;
      }

      quote.value = response?.quote || quote.value;
    } catch (error) {
      await handleError(error);
    }
  };

  /**
   * Save / Update Quote
   *
   * @param formData
   */
  const saveQuote = async () => {
    try {
      const response = await _iq.saveQuote(quoteId.value, quote.value);
      quote.value = response?.quote || quote.value;
    } catch (error) {
      await handleError(error);
    }
  };

  /**
   * Calculate quote price
   *
   * @returns
   */
  const calculatePrice = async () => {
    try {
      const response = await _iq.calculatePrice(quoteId.value, quote.value);

      jeopardies.value = response?.reasonCodes || [];
      if (jeopardies.value.length > 0 && jeopardies.value.some((j) => j.typeOfReason === 0)) {
        throw jeopardies.value.find((j) => j.typeOfReason === 0)?.code;
      }

      checkPremiumHasChanged(response?.quote);

      quote.value = response?.quote || quote.value;
    } catch (error) {
      await handleError(error);
    }
  };

  const applyRulesAndSave = async (calculateQuotePrice: boolean = true) => {
    submitQuoteErrors.value = [];
    quote.value.quotePhase = InsuranceQuotePhase.FullQuote;
    // Apply business rules
    await applyBusinessRules();

    if (calculateQuotePrice) {
      // Save Quote for Pricing
      await saveQuote();

      // Recalculate quote price
      await calculatePrice();
    }

    // Save quote with new price
    await saveQuote();
  };

  /**
   * Submit Quote
   *
   * @param formData
   */
  const submitQuote = async (submitQuote: ISubmitQuote) => {
    try {
      submitQuoteErrors.value = [];
      const response = await _iq.submitQuote(quoteId.value, submitQuote);

      quote.value = response?.quote || quote.value;
      submitQuoteErrors.value = response?.errors || [];
      //DSQ-2268 INC0216952  if the policy number is generated than don't have to throw an error -- should go to confirmation screen
      const hasPolicyNumberAlreadyGenerated = response.quote.policyReference && response.quote.policyReference != '';
      if (!response.success && submitQuoteErrors?.value?.length > 0 && !hasPolicyNumberAlreadyGenerated) {
        throw new Error(submitQuoteErrors.value[0].errorDescription);
      }

      if (quoteComplete(response) && quote?.value?.id) {
        setQuoteSubmitted(quote.value.id.toString());
      }

      return response;
    } catch (error) {
      await handleError(error);
    }
  };

  /**
   * DSQ-308 primary policyholder must agree t&c if postal address changed
   */
  const checkUpdatePostalAddressRequired = async (): Promise<boolean> => {
    const partyId = quote.value.primaryPolicyHolderDetails?.partyId?.value;
    const dpid = quote.value.primaryPolicyHolderDetails?.dpid?.value as number;
    if (!dpid && partyId) {
      return true; // show t&c if manual / oversea address, only if it's been party matched.
    } else if (!partyId) {
      return false; // hide t&c for new contact
    }
    const res = await _iq.checkUpdatePostalAddressRequired(partyId, dpid);
    updatePostalAddressRequired.value = res ?? false;
    return res;
  };

  const updatePostalAddress = async (): Promise<boolean> => {
    const partyId = quote.value.primaryPolicyHolderDetails?.partyId?.value;
    if (!partyId) return false;

    const postalAddress = mapPrimaryPolicyholderAddress(
      quote.value?.primaryPolicyHolderDetails as IPolicyHolderDetails
    );

    const res = await _iq.updatePostalAddress(partyId, postalAddress);

    return res;
  };

  const handleError = async (error: any) => {
    const isErrorArray = isArray(error);
    if (!isErrorArray && error?.code) {
      submitQuoteErrors.value = [error.code];
      throw error.code;
    }

    if (isErrorArray && error?.length > 0) {
      submitQuoteErrors.value = error[0]?.code;
    }

    throw error;
  };

  /**
   * Push data to analytics
   *
   * @param options
   */
  const pushAnalytics = (options: { event: FormEvents }) => {
    analyticsStore.pushAnalytics({
      event: options.event,
      form: {},
    });
  };

  const checkPremiumHasChanged = (newQuote: Partial<IQuote>) => {
    premiumHasChanged.value =
      newQuote?.insuranceQuoteSummary?.yearlyAnnualPremium != quote.value.insuranceQuoteSummary?.yearlyAnnualPremium;
  };

  const resetPremiumHasChanged = () => {
    premiumHasChanged.value = false;
  };

  return {
    isMC,
    isMT,
    isMF,
    quote,
    quoteId,
    sessionId,
    quoteType,
    originalQuote,
    updatePostalAddressRequired,
    jeopardies,
    checkUpdatePostalAddressRequired,
    updatePostalAddress,
    fetchQuote,
    createQuote,
    applyBusinessRules,
    saveQuote,
    calculatePrice,
    applyRulesAndSave,
    submitQuote,
    submitQuoteErrors,
    pushAnalytics,
    refreshOriginalQuote,
    premiumHasChanged,
    resetPremiumHasChanged,
  };
});
